import React, { useState, useEffect } from "react";
import LogoImg from "../../assets/images/Logo.png";
import base64 from "base64-encode-file";
import CACHistory from "./History/CACHistory.jsx";
import {
  Form,
  Input,
  Select,
  DatePicker,
  Button,
  message,
  Steps,
  Upload,
} from "antd";
import {
  InboxOutlined,
  LoadingOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import { config } from "../../config/config";
import CryptoJS from "crypto-js";
import moment from "moment";
import axios from "axios";

const MAX_FILE_SIZE = 50 * 1024; // 50KB in bytes
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

const REGISTRATION_TYPES = [
  { value: "Business Name (BN)", label: "Business Name (BN)", price: 25000 },
  {
    value: "Limited Liability Company (LLC)",
    label: "Limited Liability Company (LLC)",
    price: 25000,
  },
];

const customUploadStyle = `
  .full-width-uploader .ant-upload-select {
    width: 100% !important;
    height: 150px !important;
  }
  .full-width-uploader .ant-upload-list-item-container {
    width: 150px !important;
    height: 150px !important;
  }
  .full-width-uploader .ant-upload-list-item,
  .full-width-uploader .ant-upload-list-item-info,
  .full-width-uploader .ant-upload-list-item-info .ant-image,
  .full-width-uploader .ant-upload-list-item-info img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }
`;

function CAC() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  // fileList may hold File objects when selected in-session.
  const [fileList, setFileList] = useState({
    passport: null,
    idCard: null,
    signature: null,
    ninUpload: null,
  });
  // base64Files stores data URLs (data:image/..;base64,...) so previews and submission
  // can survive a reload. previewUrls used for image src (can be object URL or data URL)
  const [base64Files, setBase64Files] = useState({
    passport: null,
    idCard: null,
    signature: null,
    ninUpload: null,
  });
  const [previewUrls, setPreviewUrls] = useState({
    passport: null,
    idCard: null,
    signature: null,
    ninUpload: null,
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedState, setSelectedState] = useState("");
  const [stateOptions, setStateOptions] = useState([]);
  const [lgaOptions, setLgaOptions] = useState([]);
  // Add this state to store form data
  const [formData, setFormData] = useState({}); // Stores accumulated form data across steps

  // Initialize with the first registration type's details
  const DEFAULT_REGISTRATION_TYPE = REGISTRATION_TYPES[0];
  const [registrationAmount, setRegistrationAmount] = useState(
    DEFAULT_REGISTRATION_TYPE.price
  );
  const [selectedCategoryLabel, setSelectedCategoryLabel] = useState(
    DEFAULT_REGISTRATION_TYPE.label
  );
  const [priceLoading, setPriceLoading] = useState(true);
  const [prices, setPrices] = useState({
    cac_bn: 25000,
    cac_llc: 25000,
  });

  useEffect(() => {
    const fetchPrices = async () => {
      setPriceLoading(true);
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
        if (Array.isArray(response.data)) {
          const bnPricing = response.data.find((item) => item.key === "cac_bn");
          const llcPricing = response.data.find((item) => item.key === "cac_llc");
          
          const newPrices = {
            cac_bn: bnPricing?.prices?.agent ?? 25000,
            cac_llc: llcPricing?.prices?.agent ?? 25000,
          };
          setPrices(newPrices);
          
          // Set initial amount based on current default form registration type
          const currentType = form.getFieldValue("registrationType") || DEFAULT_REGISTRATION_TYPE.value;
          const pricingKey = currentType === "Business Name (BN)" ? "cac_bn" : "cac_llc";
          const initialPrice = newPrices[pricingKey];
          setRegistrationAmount(initialPrice);
          form.setFieldsValue({ amount: initialPrice });
        }
      } catch (error) {
        message.error("Failed to fetch current prices");
      } finally {
        setPriceLoading(false);
      }
    };
    fetchPrices();
  }, [form]);

  const steps = [
    {
      title: "Personal Details",
      description: "Proprietor information",
    },
    {
      title: "Home Address",
      description: "Your residential address",
    },
    {
      title: "Business Details",
      description: "Name, nature, and address",
    },
    {
      title: "Document Upload",
      description: "Upload required documents",
    },
  ];

  useEffect(() => {
    fetch("https://nga-states-lga.onrender.com/fetch")
      .then((res) => res.json())
      .then((data) => setStateOptions(data))
      .catch((error) => {
        setStateOptions([]);
      });
  }, []);

  const handleStateChange = async (value, type) => {
    try {
      const response = await fetch(
        `https://nga-states-lga.onrender.com/?state=${value}`
      );
      const lgas = await response.json();

      if (type === "business") {
        setLgaOptions(lgas);
        form.setFieldValue("LGAofBusiness", ""); // Reset LGA when state changes
      } else if (type === "origin") {
        setLgaOptions(lgas);
        form.setFieldValue("lgaOfOrigin", ""); // Reset LGA when state changes
      }
    } catch (error) {
      setLgaOptions([]);
    }
  };

  const handleRegistrationTypeChange = (value) => {
    const pricingKey = value === "Business Name (BN)" ? "cac_bn" : "cac_llc";
    const selectedPrice = prices[pricingKey] || 25000;
    setRegistrationAmount(selectedPrice);
    const selectedType = REGISTRATION_TYPES.find(
      (type) => type.value === value
    );
    if (selectedType) {
      setSelectedCategoryLabel(selectedType.label);
    }
    form.setFieldsValue({ amount: selectedPrice });
  };

  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  const decryptData = (ciphertext) => {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  };

  // Update the nextStep function
  const nextStep = async () => {
    try {
      // Validate current step fields
      await form.validateFields();

      // Get current form values and merge with existing data
      const currentValues = form.getFieldsValue();
      setFormData((prevData) => {
        const merged = {
          ...prevData,
          ...currentValues,
        };
        // save on moving forward
        saveToLocal({ formData: merged });
        return merged;
      });

      const next = currentStep + 1;
      setCurrentStep(next);
      saveToLocal({ currentStep: next });
    } catch (error) {
      message.error("Please fill in all required fields");
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);

      // Helper to convert data URL to File object for FormData
      const dataURLtoFile = (dataurl, filename) => {
        if (!dataurl) return null;
        let arr = dataurl.split(","),
          mime = arr[0].match(/:(.*?);/)[1],
          bstr = atob(arr[1]),
          n = bstr.length,
          u8arr = new Uint8Array(n);
        while (n--) {
          u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
      };

      // Merge final step data with accumulated data
      const allFormData = {
        ...formData,
        ...values,
      };

      // Check for required files
      // allow relying on stored base64Files if File objects not present
      if (
        (!fileList.passport && !base64Files.passport) ||
        (!fileList.signature && !base64Files.signature)
      ) {
        throw new Error("Please upload passport and signature");
      }

      // Get user ID
      const userStr = localStorage.getItem("user");
      if (!userStr) {
        throw new Error("User not found. Please login again.");
      }
      const userObj = decryptData(userStr);
      const userId = userObj?._id || userObj?.id;

      const payload = new FormData();
      payload.append("userId", userId);
      payload.append("registrationType", allFormData.registrationType);
      payload.append("amount", allFormData.amount);
      // Step 1: Business Info
      payload.append("businessName1", allFormData.businessName1);
      payload.append("businessName2", allFormData.businessName2 || "");
      payload.append("businessName3", allFormData.businessName3 || "");
      payload.append(
        "dateOfCommencement",
        allFormData.dateOfCommencement
          ? moment(allFormData.dateOfCommencement).format("YYYY-MM-DD")
          : ""
      );
      payload.append("natureOfBusiness1", allFormData.natureOfBusiness1);
      payload.append("natureOfBusiness2", allFormData.natureOfBusiness2 || "");

      // Step 2: Business Location
      payload.append("businessAddress", allFormData.AddressofBusiness);
      payload.append(
        "businessAddressDescription",
        allFormData.businessAddressDescription
      );
      payload.append("businessCity", allFormData.BusinessCity);
      payload.append("businessLGA", allFormData.LGAofBusiness);
      payload.append("businessState", allFormData.businessState);
      payload.append("branchAddress", allFormData.branchAddress || "");
      payload.append("postalCode", allFormData.postalCode || "");
      // Step 3: Personal Details
      payload.append("surname", allFormData.surname);
      payload.append("firstName", allFormData.firstName);
      payload.append("otherName", allFormData.otherName || "");
      payload.append("formerName", allFormData.formerName || "");
      payload.append("nationality", allFormData.nationality);
      payload.append("formerNationality", allFormData.formerNationality || "");
      payload.append("occupation", allFormData.occupation);
      payload.append("gender", allFormData.gender);
      payload.append("phoneNumber", allFormData.phone);
      payload.append("email", allFormData.email);
      payload.append("homeAddress", allFormData.homeAddress);
      payload.append(
        "homeAddressDescription",
        allFormData.homeAddressDescription
      );
      payload.append("cityOfOrigin", allFormData.cityOfOrigin);
      payload.append("stateOfOrigin", allFormData.stateOfOrigin);
      payload.append("lgaOfOrigin", allFormData.lgaOfOrigin);

      // Step 4: Identity Verification
      payload.append("identityType", allFormData.identityType);
      payload.append("identityNumber", allFormData.identityNumber);
      payload.append(
        "dateOfBirth",
        allFormData.dateOfBirth
          ? moment(allFormData.dateOfBirth).format("YYYY-MM-DD")
          : ""
      );

      // Append files
      payload.append("passport", fileList.passport || dataURLtoFile(base64Files.passport, "passport.jpg"));
      payload.append("idCard", fileList.idCard || dataURLtoFile(base64Files.idCard, "idCard.jpg") || "");
      payload.append("signature", fileList.signature || dataURLtoFile(base64Files.signature, "signature.jpg"));
      payload.append("ninUpload", fileList.ninUpload || dataURLtoFile(base64Files.ninUpload, "ninUpload.jpg") || "");

      // Validate required dates before submission

      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.cacRegistration}`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          withCredentials: true,
        }
      );

      

      await Swal.fire({
        icon: "success",
        title: "Registration Successful!",
        text:
          response.data.message ||
          "Your business registration has been submitted successfully.",
        confirmButtonColor: "#f59e0b",
      });

      // Reset everything
      form.resetFields();
      setRefreshKey((prev) => prev + 1);
      setFileList({
        passport: null,
        idCard: null,
        signature: null,
        ninUpload: null,
      });
      setPreviewUrls({
        passport: null,
        idCard: null,
        signature: null,
        ninUpload: null,
      });
      setFormData({}); // Clear accumulated form data
      setCurrentStep(0);
      setBase64Files({
        passport: null,
        idCard: null,
        signature: null,
        ninUpload: null,
      });
      // clear saved state
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      
      await Swal.fire({
        icon: "error",
        title: "Registration Failed",
        text:
          error.response?.data?.message ||
          error.message ||
          "Something went wrong",
        confirmButtonColor: "#f59e0b",
      });
    } finally {
      setLoading(false);
    }
  };

  // Function to handle step changes
  const prevStep = () => {
    // Save current step data before going back
    const currentValues = form.getFieldsValue();
    setFormData((prevData) => {
      const merged = {
        ...prevData,
        ...currentValues,
      };
      saveToLocal({ formData: merged });
      return merged;
    });

    const prev = currentStep - 1;
    setCurrentStep(prev);
    saveToLocal({ currentStep: prev });
  };

  // Add file upload handler
  const handleFileChange = async (e, type) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate file type
        if (!ALLOWED_FILE_TYPES.includes(file.type)) {
          message.error("Only JPG, JPEG and PNG files are allowed");
          return;
        }

        // Validate file size
        if (file.size > MAX_FILE_SIZE) {
          message.error("File size must be less than 50KB");
          return;
        }

        // Create preview URL
        // get data URL for preview and storage
        const dataUrl = await base64(file); // this returns 'data:image/...;base64,...'
        // create object URL for immediate display if you prefer, otherwise use dataUrl
        const preview = dataUrl;
        setPreviewUrls((prev) => ({
          ...prev,
          [type]: preview,
        }));

        // store base64 dataURL so we can restore after reload
        setBase64Files((prev) => ({
          ...prev,
          [type]: dataUrl,
        }));

        // Also keep the actual File in memory (for current session submission)
        setFileList((prev) => ({
          ...prev,
          [type]: file,
        }));

        // persist to localStorage
        saveToLocal({
          base64Files: {
            ...base64Files,
            [type]: dataUrl,
          },
          previewUrls: {
            ...previewUrls,
            [type]: preview,
          },
        });
      } catch (error) {
        message.error(`Failed to upload ${type}`);
      }
    }
  };

  // First, add a handleRemoveFile function after the handleFileChange function
  const handleRemoveFile = (type) => {
    setFileList((prev) => ({
      ...prev,
      [type]: null,
    }));
    setPreviewUrls((prev) => ({
      ...prev,
      [type]: null,
    }));
    setBase64Files((prev) => ({
      ...prev,
      [type]: null,
    }));
    saveToLocal({
      base64Files: {
        ...base64Files,
        [type]: null,
      },
      previewUrls: {
        ...previewUrls,
        [type]: null,
      },
    });
  };

  // localStorage key & 24h expiration helper
  const STORAGE_KEY = "cacFormSaved";
  const HOURS_24 = 24 * 60 * 60 * 1000;
  const DATE_KEYS = ["dateOfBirth", "dateOfCommencement"];

  const saveToLocal = (override = {}) => {
    try {
      // Ensure date fields are saved as ISO strings (so they serialize/restore reliably)
      const fvRaw = form.getFieldsValue();
      const fv = { ...fvRaw };
      DATE_KEYS.forEach((k) => {
        if (fv[k]) {
          fv[k] = moment.isMoment(fv[k]) ? fv[k].toISOString() : fv[k];
        }
      });

      const payload = {
        timestamp: Date.now(),
        currentStep,
        formValues: fv,
        base64Files,
        previewUrls,
        formData,
        ...override,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch (e) {}
  };

  // Load saved state on mount (if not expired)
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (!saved || !saved.timestamp) return;
      if (Date.now() - saved.timestamp > HOURS_24) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Restore form values
      if (saved.formValues) {
        // convert known date strings back to moment objects
        const restored = { ...saved.formValues };
        DATE_KEYS.forEach((k) => {
          if (restored[k]) {
            // If it's already a moment keep it, otherwise try to parse
            restored[k] = moment.isMoment(restored[k])
              ? restored[k]
              : moment(restored[k]);
          }
        });
        form.setFieldsValue(restored);
      }

      // Restore base64 previews and previewUrls
      if (saved.base64Files) {
        setBase64Files(saved.base64Files);
        // prefer data url for preview if available
        setPreviewUrls((prev) => ({
          passport: saved.base64Files.passport || prev.passport,
          idCard: saved.base64Files.idCard || prev.idCard,
          signature: saved.base64Files.signature || prev.signature,
          ninUpload: saved.base64Files.ninUpload || prev.ninUpload,
        }));
      } else if (saved.previewUrls) {
        setPreviewUrls(saved.previewUrls);
      }

      // restore other accumulative data and step
      if (saved.formData) setFormData(saved.formData);
      if (typeof saved.currentStep === "number")
        setCurrentStep(saved.currentStep);
    } catch (e) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper to render an upload box
  const UploadBox = ({ fieldName, label, required, objectFit = "cover" }) => (
    <Form.Item
      name={fieldName}
      label={label}
      rules={required ? [{ required: true, message: `Please upload ${label}` }] : []}
    >
      <Upload
        listType="picture-card"
        className="full-width-uploader"
        showUploadList={false}
        customRequest={({ file }) =>
          handleFileChange({ target: { files: [file] } }, fieldName)
        }
      >
        {previewUrls[fieldName] ? (
          <div className="relative group w-full h-full">
            <img
              src={previewUrls[fieldName]}
              alt={label}
              style={{ width: 120, height: 120, objectFit }}
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleRemoveFile(fieldName); }}
                className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600"
              >
                Remove
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-1">
            <PlusOutlined className="text-gray-400 text-lg" />
            <span className="text-[11px] text-gray-500 text-center leading-tight">{label}</span>
            <span className="text-[10px] text-gray-400">JPG/PNG · max 50KB</span>
          </div>
        )}
      </Upload>
    </Form.Item>
  );

  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white shadow-lg border border-gray-100 overflow-hidden">
        <style>{customUploadStyle}</style>
        <style>{`
          .cac-form .ant-form-item { margin-bottom: 10px !important; }
          .cac-form .ant-form-item-label > label {
            font-weight: 600 !important;
            color: #475569 !important;
            font-size: 0.78rem !important;
            height: auto !important;
          }
          .cac-form .ant-input:not(textarea), .cac-form .ant-select-selector, .cac-form .ant-picker {
            border-radius: 8px !important;
            border-color: #e2e8f0 !important;
            font-size: 0.85rem !important;
            height: 38px !important;
          }
          .cac-form textarea.ant-input {
            border-radius: 8px !important;
            border-color: #e2e8f0 !important;
            font-size: 0.85rem !important;
          }
          .cac-form .ant-select-selector {
            align-items: center !important;
          }
          .cac-form .ant-upload.ant-upload-select { width: 100% !important; height: 120px !important; }
          .cac-form .full-width-uploader .ant-upload-select { width: 100% !important; height: 120px !important; }
        `}</style>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-5 py-3 border-b border-gray-100 bg-gradient-to-r from-sky-50 to-indigo-50">
          <div>
            <h2 className="text-base sm:text-lg font-extrabold text-slate-800">CAC Business Registration</h2>
            <p className="text-slate-500 text-xs mt-0.5">Register your business with the Corporate Affairs Commission</p>
          </div>
          <div className="self-start sm:self-auto bg-white px-3 py-1 rounded-xl border-2 border-sky-200 text-sky-600 font-black text-sm shadow-sm whitespace-nowrap">
            {priceLoading ? "Loading..." : `₦${registrationAmount.toLocaleString()}`}
          </div>
        </div>

        <div className="px-5 py-4">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            onValuesChange={() => { saveToLocal(); }}
            className="cac-form"
            initialValues={{
              registrationType: DEFAULT_REGISTRATION_TYPE.value,
              amount: DEFAULT_REGISTRATION_TYPE.price,
              nationality: "Nigerian",
            }}
          >
            {/* Hidden amount field */}
            <Form.Item name="amount" hidden><Input type="number" /></Form.Item>

            {/* ── Registration Type ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="registrationType" label="Registration Type" rules={[{ required: true }]}>
                <Select onChange={handleRegistrationTypeChange}>
                  {REGISTRATION_TYPES.map((t) => (
                    <Select.Option key={t.value} value={t.value}>{t.label}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="businessName1" label="Business Name 1" rules={[{ required: true }]}>
                <Input placeholder="Proposed business name" />
              </Form.Item>
            </div>

            {/* ── Proprietor Names ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="surname" label="Last Name" rules={[{ required: true }]}>
                <Input placeholder="Surname" />
              </Form.Item>
              <Form.Item name="firstName" label="First Name" rules={[{ required: true }]}>
                <Input placeholder="First name" />
              </Form.Item>
            </div>

            {/* ── Other Name & Nationality ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="otherName" label="Other Name">
                <Input placeholder="Middle / other name" />
              </Form.Item>
              <Form.Item name="nationality" label="Nationality" rules={[{ required: true }]}>
                <Input placeholder="e.g. Nigerian" />
              </Form.Item>
            </div>

            {/* ── DOB & Gender ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="dateOfBirth" label="Date of Birth" rules={[{ required: true }]}>
                <DatePicker className="w-full" />
              </Form.Item>
              <Form.Item name="gender" label="Gender" rules={[{ required: true }]}>
                <Select placeholder="Select gender">
                  <Select.Option value="male">Male</Select.Option>
                  <Select.Option value="female">Female</Select.Option>
                </Select>
              </Form.Item>
            </div>

            {/* ── Phone & Email ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="phone" label="Phone Number" rules={[{ required: true }]}>
                <Input placeholder="e.g. 08012345678" maxLength={11} />
              </Form.Item>
              <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
                <Input placeholder="email@example.com" />
              </Form.Item>
            </div>

            {/* ── Home Address & Business Address ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="homeAddress" label="Home Address (House No & Street)" rules={[{ required: true }]}>
                <Input placeholder="House number and street name" />
              </Form.Item>
              <Form.Item name="AddressofBusiness" label="Business Address (House No & Street)" rules={[{ required: true }]}>
                <Input placeholder="Business house number and street name" />
              </Form.Item>
            </div>

            {/* ── State of Origin & LGA ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="stateOfOrigin" label="State of Origin" rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Select state"
                  onChange={(val) => handleStateChange(val, "origin")}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {stateOptions.map((s) => (
                    <Select.Option key={s} value={s}>{s}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item name="lgaOfOrigin" label="LGA of Origin" rules={[{ required: true }]}>
                <Select
                  showSearch
                  placeholder="Select LGA"
                  disabled={!form.getFieldValue("stateOfOrigin")}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {lgaOptions.map((l) => (
                    <Select.Option key={l} value={l}>{l}</Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>

            {/* ── Nature of Business (full width description) ── */}
            <Form.Item name="natureOfBusiness1" label="Nature / Description of Business" rules={[{ required: true }]}>
              <Input.TextArea
                rows={2}
                placeholder="Describe the nature of your business"
                style={{ borderRadius: 8, fontSize: "0.85rem" }}
              />
            </Form.Item>

            {/* ── Date of Commencement & NIN ── */}
            <div className="grid grid-cols-2 gap-x-4">
              <Form.Item name="dateOfCommencement" label="Date of Commencement" rules={[{ required: true }]}>
                <DatePicker className="w-full" style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item name="identityNumber" label="NIN (National Identity Number)" rules={[{ required: true }]}>
                <Input placeholder="Enter 11-digit NIN" maxLength={11} />
              </Form.Item>
            </div>
            {/* hidden identity type — set to NIN by default */}
            <Form.Item name="identityType" hidden initialValue="NIN"><Input /></Form.Item>

            {/* ── Uploads row: Signature | Passport ── */}
            <div className="mt-2">
              <p className="text-xs font-semibold text-slate-600 mb-2 uppercase tracking-wide">Upload Documents</p>
              <div className="grid grid-cols-2 gap-3">
                <UploadBox fieldName="signature" label="Director Signature" required objectFit="contain" />
                <UploadBox fieldName="passport" label="Passport Photo" required />
              </div>
            </div>

            {/* ── Submit ── */}
            <div className="pt-3 border-t border-gray-100 mt-4">
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full h-10 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-600 border-0 font-semibold text-white"
                size="middle"
              >
                {loading ? "Submitting..." : `Submit Registration (₦${registrationAmount.toLocaleString()})`}
              </Button>
            </div>
          </Form>
        </div>
      </div>

      {/* History */}
      <div className="bg-white rounded-2xl p-4 shadow-lg border border-gray-100 mt-6">
        <CACHistory key={refreshKey} />
      </div>
    </div>
  );
}

export default CAC;

